import { ReactComponent as AmexLogo } from "@/assets/icons/amex.svg";
import { ReactComponent as MastercardLogo } from "@/assets/icons/mastercard-color.svg";
import { ReactComponent as VisaLogo } from "@/assets/icons/visa-color.svg";
import { Icon } from "@/components/atoms";
import { CardData, CardLabel, ClientPaymentMethod, PaymentMethodType } from "@/redux/apis/clientPayment/types";

/** Mirrors PaymentMethodKeysResource::MAX_CARDS_PER_ACCOUNT on the backend (DEV-1136) */
export const MAX_CARDS_PER_ACCOUNT = 5;

export const getCardBrandLogo = (issuer?: string) => {
  switch (issuer?.toLowerCase()) {
    case "visa":
      return <VisaLogo className="h-4 w-7" />;
    case "mastercard":
      return <MastercardLogo className="h-4 w-7" />;
    case "amex":
    case "american express":
      return <AmexLogo className="h-4 w-7" />;
    default:
      return <Icon name="Card" size={18} className="text-neutral-dark-gray" />;
  }
};

export const getCardIssuerDisplay = (issuer?: string) => {
  if (!issuer) return "Credit Card";
  if (issuer.toLowerCase() === "amex") return "American Express";
  return issuer.charAt(0).toUpperCase() + issuer.slice(1).toLowerCase();
};

export const getCardLabelDisplay = (label: CardLabel) => (label === CardLabel.CORPORATE ? "Business" : "Personal");

export const getCardLastFour = (cardData: CardData | null) => {
  const digits = cardData?.number?.replace(/\D/g, "") ?? "";
  return digits.slice(-4);
};

export const isCreditCard = (method: ClientPaymentMethod) => method.type === PaymentMethodType.CREDIT_CARD;

export const getCardNicknameDisplay = (method: ClientPaymentMethod) =>
  method.card_data?.nickname?.trim() || getCardIssuerDisplay(method.card_data?.issuer);

export const getCardExpiry = (cardData: CardData | null) => {
  if (!cardData?.expiry_month || !cardData?.expiry_year) return "";
  const mm = String(cardData.expiry_month).padStart(2, "0");
  const yy = String(cardData.expiry_year).slice(-2);
  return `${mm}/${yy}`;
};

/**
 * Normalises the API's expiry fields. The V3 payment-method resource serialises both as strings
 * (CreditCardData::$expiryYear is a PHP string), and legacy pre-Stripe rows store the year as two
 * digits ("16"), which `new Date(16, …)` would read as the year 1916 — i.e. always expired.
 */
const parseCardExpiry = (cardData: CardData | null): { month: number; year: number } | null => {
  const month = Number(cardData?.expiry_month);
  const rawYear = Number(cardData?.expiry_year);
  if (!Number.isInteger(month) || month < 1 || month > 12 || !Number.isInteger(rawYear) || rawYear <= 0) return null;
  return { month, year: rawYear < 100 ? 2000 + rawYear : rawYear };
};

/** A card is valid through the last day of its expiry month. Unparseable expiry → not expired (matches the backend). */
export const isCardExpired = (cardData: CardData | null, now: Date = new Date()) => {
  const expiry = parseCardExpiry(cardData);
  if (!expiry) return false;
  const firstDayAfterExpiryMonth = new Date(expiry.year, expiry.month, 1);
  return now >= firstDayAfterExpiryMonth;
};

/** Invoice methods are always usable; a card is usable until it expires. */
export const isUsablePaymentMethod = (method: ClientPaymentMethod, now: Date = new Date()) =>
  !isCreditCard(method) || !isCardExpired(method.card_data, now);

/**
 * The method to preselect for a new booking (DEV-1596): the account default while it is still usable,
 * else the first usable method, else null — nothing is silently booked against an expired card.
 */
export const pickDefaultPaymentMethod = (methods: ClientPaymentMethod[], now: Date = new Date()): ClientPaymentMethod | null =>
  methods.find((method) => method.default && isUsablePaymentMethod(method, now)) ??
  methods.find((method) => isUsablePaymentMethod(method, now)) ??
  null;
